import { useFilterContext } from 'contexts/FilterContext';
import React, {
	createContext,
	useContext,
	useReducer,
	useEffect,
	useCallback,
} from 'react';
import {
	getAvgMarginByGrossSpendByGroupData,
	getAvgMarginByJobTitleData,
	getAvgMarginBySupplierData,
	getBLSCityMapData,
	getBLSStateMapData,
	getBLSgrowthChartData,
	getBoxWhiskerClientData,
	getBoxWhiskerData,
	getMarkupData,
	getSpendBreakdown,
	getSpendByPaycode,
	getTopState,
} from 'utils/apiRequests';
import {
	generateBLSData,
	generateToolTip,
	getMarkUpChartData,
	getSpendBreakdownFormattedData,
	stateColorMap,
} from 'utils/filterNames';
import _ from 'lodash';
import * as Highchartss from 'highcharts';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
// Define the initial state
const initialState = {
	chartData1: [],
	chartData2: [],
	loading: false,
	error: null,
	blscityLoading: false,
	blsstateLoading: false,
	boxplotclientLoading: false,
	scatterplotLoading: false,
	markupLoading: false,
	boxplotblsLoading: false,
	spendBreakdownLoading: false,
	topStatesLoading: false,
	avgMarginBySupplierLoading: false,
	avgMarginGrossSpendLoading: false,
	avgMarginByJobTitleLoading: false,
	type: 'FETCH_CHART_DATA_SUCCESS',
	markupChartData: [],
	markupCategories: [],
	markupSuppliers: [],
	scatterPlotData: [],
	boxPlotData: {
		boxplotData: [],
		scatterData: [],
		xaxis: [],
	},
	blsCityChartData: [],
	blsStateChartData: [],
	blsStateDrilldownData: [],
	boxPlotClientData: {
		boxplotData: [],
		scatterData: [],
		xaxis: [],
	},
	spendBreakdownData: [],
	spendByPaycodeData: [],
	topStatesData: [],
	avgMarginBySupplierData: [],
	avgMarginGrossSpendData: [],
	avgMarginByJobTitleData: [],
};
// Define actions
const actionTypes = {
	SET_CHART_DATA: 'SET_CHART_DATA',
	FETCH_CHART_DATA_REQUEST: 'FETCH_CHART_DATA_REQUEST',
	FETCH_CHART_DATA_SUCCESS: 'FETCH_CHART_DATA_SUCCESS',
	FETCH_CHART_DATA_FAILURE: 'FETCH_CHART_DATA_FAILURE',
	// Add more actions as needed
	SET_SCATTERPLOT_LOADER: 'SET_SCATTERPLOT_LOADER',
	SET_BOXPLOTCLIENT_LOADER: 'SET_BOXPLOTCLIENT_LOADER',
	SET_BOXPLOTBLS_LOADER: 'SET_BOXPLOTBLS_LOADER',
	SET_BLSSTATE_LOADER: 'SET_BLSSTATE_LOADER',
	SET_BLSCITY_LOADER: 'SET_BLSCITY_LOADER',
	SET_MARKUP_LOADER: 'SET_MARKUP_LOADER',
	SET_SPENDBREAKDOWN_LOADER: 'SET_SPENDBREAKDOWN_LOADER',
	SET_SPENDBYPAYCODE_LOADER: 'SET_SPENDBYPAYCODE_LOADER',
	SET_TOPSTATES_LOADER: 'SET_TOPSTATES_LOADER',
	SET_AVGMARGINBYSUPPLIER_LOADER: 'SET_AVGMARGINBYSUPPLIER_LOADER',
	SET_AVGMARGINGROSSSPEND_LOADER: 'SET_AVGMARGINGROSSSPEND_LOADER',
	SET_AVGMARGINBYJOBTITLE_LOADER: 'SET_AVGMARGINBYJOBTITLE_LOADER',
};

// Define the reducer function
const chartReducer = (state, action) => {
	switch (action.type) {
		case actionTypes.SET_CHART_DATA:
			return {
				...state,
				[action.chartName]: action.chartData,
			};
		case actionTypes.FETCH_CHART_DATA_REQUEST:
			return {
				...state,
				loading: true,
				error: null,
			};
		case actionTypes.FETCH_CHART_DATA_SUCCESS:
			return {
				...state,
				...action,
				[action.chartName]: action.chartData,
				loading: false,
			};
		case actionTypes.FETCH_CHART_DATA_FAILURE:
			return {
				...state,
				loading: false,
				error: action.error,
			};
		case actionTypes.SET_SCATTERPLOT_LOADER:
			return {
				...state,
				scatterplotLoading: action.loading,
			};
		case actionTypes.SET_BOXPLOTCLIENT_LOADER:
			return {
				...state,
				boxplotclientLoading: action.loading,
			};
		case actionTypes.SET_BOXPLOTBLS_LOADER:
			return {
				...state,
				boxplotblsLoading: action.loading,
			};
		case actionTypes.SET_BLSSTATE_LOADER:
			return {
				...state,
				blsstateLoading: action.loading,
			};
		case actionTypes.SET_BLSCITY_LOADER:
			return {
				...state,
				blscityLoading: action.loading,
			};
		case actionTypes.SET_MARKUP_LOADER:
			return {
				...state,
				markupLoading: action.loading,
			};
		case actionTypes.SET_SPENDBREAKDOWN_LOADER:
			return {
				...state,
				spendBreakdownLoading: action.loading,
			};
		case actionTypes.SET_SPENDBYPAYCODE_LOADER:
			return {
				...state,
				spendByPaycodeLoading: action.loading,
			};
		case actionTypes.SET_TOPSTATES_LOADER:
			return {
				...state,
				topStatesLoading: action.loading,
			};
		case actionTypes.SET_AVGMARGINBYSUPPLIER_LOADER:
			return {
				...state,
				avgMarginBySupplierLoading: action.loading,
			};
		case actionTypes.SET_AVGMARGINGROSSSPEND_LOADER:
			return {
				...state,
				avgMarginGrossSpendLoading: action.loading,
			};
		case actionTypes.SET_AVGMARGINBYJOBTITLE_LOADER:
			return {
				...state,
				avgMarginByJobTitleLoading: action.loading,
			};
		// Handle other actions if needed
		default:
			return state;
	}
};

// Create the context
const ChartContext = createContext();

// Create the context provider component
const ChartContextProvider = ({ children }) => {
	const [state, dispatch] = useReducer(chartReducer, initialState);
	const { state: filterValues } = useFilterContext();
	// Define action for setting chart data
	const setChartData = useCallback((chartName, chartData) => {
		dispatch({ type: actionTypes.SET_CHART_DATA, chartName, chartData });
	}, []);
	const setLoader = useCallback(isLoading => {
		dispatch({ type: actionTypes.SET_BLSSTATE_LOADER, loading: isLoading });
	}, []);

	useEffect(
		() => {
			if (
				filterValues.scatterPlotState?.length > 0 &&
				filterValues.scatterPlotWorkerType?.length > 0 &&
				filterValues.scatterPlotEmpTotalRange?.length > 0 &&
				filterValues.scatterPlotCagr &&
				filterValues.scatterPlotBLSYear?.length > 0
			) {
				const bodyParam = {
					state: filterValues.scatterPlotState,
					workerType: filterValues.scatterPlotWorkerType,
					totalEmpFrom: filterValues.scatterPlotEmpTotalRange[0],
					totalEmpTo: filterValues.scatterPlotEmpTotalRange[1],
					cagr: filterValues.scatterPlotCagr.value,
					blsYr: filterValues.scatterPlotBLSYear,
				};
				dispatch({ type: actionTypes.SET_SCATTERPLOT_LOADER, loading: true });

				getBLSgrowthChartData(bodyParam)
					.then(response => {
						dispatch({
							type: actionTypes.FETCH_CHART_DATA_SUCCESS,
							scatterPlotData:
								response.data.map(d => {
									return {
										...d,
										x: d.h_MEDIAN,
										y: d.cagr,
										state: d.areA_TITLE.split(',')[1],
										tooltip: generateToolTip(d),
										color: stateColorMap[d.areA_TITLE.split(',')[1].trim()],
									};
								}) || [],
						});
					})
					.then(() =>
						dispatch({
							type: actionTypes.SET_SCATTERPLOT_LOADER,
							loading: false,
						})
					);
			}
		},
		[
			filterValues.scatterPlotState,
			filterValues.scatterPlotWorkerType,
			filterValues.scatterPlotEmpTotalRange,
			filterValues.scatterPlotCagr,
			filterValues.scatterPlotBLSYear,
		]
	);

	useEffect(
		() => {
			if (
				filterValues.stackedBarChartJobClass?.length > 0 &&
				filterValues.stackedBarChartGrossAmountRange?.length > 0
			) {
				dispatch({ type: actionTypes.SET_MARKUP_LOADER, loading: true });
				const markupParams = {
					jobClass: filterValues.stackedBarChartJobClass,
					grossTo: Math.floor(filterValues.stackedBarChartGrossAmountRange[1]),
					grossFrom: Math.ceil(filterValues.stackedBarChartGrossAmountRange[0]),
				};
				getMarkupData(markupParams)
					.then(response => {
						const { markupdata, markupCategoriesArray } = getMarkUpChartData(
							response.data
						);
						dispatch({
							type: actionTypes.FETCH_CHART_DATA_SUCCESS,
							markupChartData: markupdata.sort((a, b) => b.total - a.total),
							markupCategories: markupCategoriesArray.sort(),
							markupSuppliers: markupdata
								.sort((a, b) => b.total - a.total)
								.map(markup => markup.name),
						});
					})
					.then(() =>
						dispatch({ type: actionTypes.SET_MARKUP_LOADER, loading: false })
					);
			}
		},
		[
			filterValues.stackedBarChartJobClass,
			filterValues.stackedBarChartGrossAmountRange,
		]
	);

	useEffect(
		() => {
			dispatch({ type: actionTypes.SET_BLSCITY_LOADER, loading: true });
			if (
				filterValues.mapCityBLSYear?.length > 0 &&
				filterValues.mapCityWorkerType?.length > 0
			) {
				const cityParams = {
					blsYr: filterValues.mapCityBLSYear,
					workerType: filterValues.mapCityWorkerType,
				};
				getBLSCityMapData(cityParams)
					.then(response => {
						dispatch({
							type: actionTypes.FETCH_CHART_DATA_SUCCESS,
							blsCityChartData:
								response.data.map(d => {
									return {
										...d,
										lon: d.lng,
										z: d.suM_TOT_EMP,
										value: d.avG_H_MEDIAN,
										tooltip: generateToolTip(d),
									};
								}) || [],
						});
					})
					.then(() =>
						dispatch({ type: actionTypes.SET_BLSCITY_LOADER, loading: false })
					);
			}
		},
		[filterValues.mapCityBLSYear, filterValues.mapCityWorkerType]
	);

	useEffect(
		() => {
			dispatch({ type: actionTypes.SET_BLSSTATE_LOADER, loading: true });
			if (
				filterValues.mapStateBLSYear?.length > 0 &&
				filterValues.mapStateWorkerType?.length > 0
			) {
				const stateParams = {
					blsYr: filterValues.mapStateBLSYear,
					workerType: filterValues.mapStateWorkerType,
				};
				getBLSStateMapData(stateParams)
					.then(async response => {
						const formatttedData = generateBLSData(response.data);
						const topology = await fetch(
							'https://code.highcharts.com/mapdata/countries/us/us-all.geo.json'
						).then(response => response.json());

						const data = Highchartss.geojson(topology);
						const newData = data.map(data => ({
							...data,
							...formatttedData.find(
								obj => obj.stateCode === data.properties.fips
							),
						}));
						dispatch({
							type: actionTypes.FETCH_CHART_DATA_SUCCESS,

							blsStateChartData: newData,
							blsStateDrilldownData: response.data,
						});
					})
					.then(() =>
						dispatch({ type: actionTypes.SET_BLSSTATE_LOADER, loading: false })
					);
			}
		},
		[filterValues.mapStateBLSYear, filterValues.mapStateWorkerType]
	);

	useEffect(
		() => {
			dispatch({ type: actionTypes.SET_BOXPLOTCLIENT_LOADER, loading: true });

			if (
				filterValues.boxWhiskerClientJobClass?.length > 0 &&
				filterValues.boxWhiskerClientSumSpendOverJobTitleRange?.length > 0
			) {
				const boxPlotClientParams = {
					jobClass: filterValues.boxWhiskerClientJobClass,
					sumSpendOverJobTitleFrom: Math.floor(
						filterValues.boxWhiskerClientSumSpendOverJobTitleRange[0]
					),
					sumSpendOverJobTitleTo: Math.ceil(
						filterValues.boxWhiskerClientSumSpendOverJobTitleRange[1]
					),
				};
				getBoxWhiskerClientData(boxPlotClientParams)
					.then(response => {
						dispatch({
							type: actionTypes.FETCH_CHART_DATA_SUCCESS,
							boxPlotClientData: {
								...response.data,
								boxplotData:
									_.sortBy(response.data.boxplotData, [
										function(o) {
											return o.jobTitle;
										},
									]) || [],
								scatterData:
									response.data.scatterData.map(obj => ({
										x: Array.from(
											new Set(
												response.data.scatterData.map(obj => obj.jobTitle)
											)
										)
											.sort()
											.findIndex(sublist => sublist === obj.jobTitle),
										y: obj.billRate,
										tooltip: generateToolTip(obj),
										rateCardMax: obj.rateCardMax,
									})) || [],
								xaxis:
									Array.from(
										new Set(response.data.scatterData.map(obj => obj.jobTitle))
									).sort() || [],
							},
						});
					})
					.then(() =>
						dispatch({
							type: actionTypes.SET_BOXPLOTCLIENT_LOADER,
							loading: false,
						})
					);
			}
		},
		[
			filterValues.boxWhiskerClientJobClass,
			filterValues.boxWhiskerClientSumSpendOverJobTitleRange,
		]
	);

	useEffect(
		() => {
			if (
				filterValues.boxWhiskerBLSYear?.length > 0 &&
				filterValues.boxWhiskerBLSWorkerType?.length > 0
			) {
				dispatch({ type: actionTypes.SET_BOXPLOTBLS_LOADER, loading: true });
				const boxPlotParams = {
					blsYr: filterValues.boxWhiskerBLSYear,
					workerType: filterValues.boxWhiskerBLSWorkerType,
				};
				getBoxWhiskerData(boxPlotParams)
					.then(response => {
						dispatch({
							type: actionTypes.FETCH_CHART_DATA_SUCCESS,
							boxPlotData: {
								...response.data,
								boxplotData:
									_.sortBy(response.data.boxplotData, [
										function(o) {
											return o.state;
										},
									]) || [],
								scatterData:
									response.data.scatterData.map(obj => ({
										x: Array.from(
											new Set(response.data.scatterData.map(obj => obj.state))
										)
											.sort()
											.findIndex(sublist => sublist === obj.state),
										y: obj.h_MEDIAN,
										tooltip: generateToolTip(obj),
									})) || [],
								xaxis:
									Array.from(
										new Set(response.data.scatterData.map(obj => obj.state))
									).sort() || [],
							},
						});
					})
					.then(() =>
						dispatch({
							type: actionTypes.SET_BOXPLOTBLS_LOADER,
							loading: false,
						})
					);
			}
		},
		[filterValues.boxWhiskerBLSYear, filterValues.boxWhiskerBLSWorkerType]
	);

	useEffect(() => {
		dispatch({ type: actionTypes.SET_SPENDBREAKDOWN_LOADER, loading: true });
		getSpendBreakdown()
			.then(response => {
				dispatch({
					type: actionTypes.FETCH_CHART_DATA_SUCCESS,
					spendBreakdownData:
						getSpendBreakdownFormattedData(response.data) || [],
				});
			})
			.then(() =>
				dispatch({
					type: actionTypes.SET_SPENDBREAKDOWN_LOADER,
					loading: false,
				})
			);
	}, []);

	useEffect(() => {
		dispatch({ type: actionTypes.SET_SPENDBYPAYCODE_LOADER, loading: true });
		getSpendByPaycode()
			.then(response => {
				dispatch({
					type: actionTypes.FETCH_CHART_DATA_SUCCESS,
					spendByPaycodeData:
						response.data.map(data => ({
							name: data.aP_PayCode,
							y: data.gross_Amount,
							tooltip: [
								{
									name: data.aP_PayCode,
									value: formatSpendWithDecimalAndSymbol(
										data.gross_Amount,
										'$',
										false,
										1
									),
								},
							],
						})) || [],
				});
			})
			.then(() =>
				dispatch({
					type: actionTypes.SET_SPENDBYPAYCODE_LOADER,
					loading: false,
				})
			);
	}, []);

	useEffect(() => {
		dispatch({ type: actionTypes.SET_TOPSTATES_LOADER, loading: true });
		getTopState()
			.then(response => {
				dispatch({
					type: actionTypes.FETCH_CHART_DATA_SUCCESS,
					topStatesData:
						response.data.map(data => ({
							name: data.aP_State,
							value: data.totalAmount,
							colorValue: data.totalAmount,
							tooltip: [
								{
									name: data.aP_State,
									value: formatSpendWithDecimalAndSymbol(
										data.totalAmount,
										'$',
										false,
										1
									),
								},
								{
									name: 'W.Average Median',
									value: `${(data.markUp * 100).toFixed(2)}%`,
								},
							],
						})) || [],
				});
			})
			.then(() =>
				dispatch({
					type: actionTypes.SET_TOPSTATES_LOADER,
					loading: false,
				})
			);
	}, []);

	useEffect(() => {
		dispatch({
			type: actionTypes.SET_AVGMARGINBYSUPPLIER_LOADER,
			loading: true,
		});
		getAvgMarginBySupplierData()
			.then(response => {
				const categoriesResult = [
					...response.data
						.map(obj => obj.supplierCategory)
						.filter(category => !category.includes('Other')),
					...response.data
						.map(obj => obj.supplierCategory)
						.filter(category => category.includes('Other')),
				];

				const weightedAvgMargin =
					[
						...response.data.map(obj => {
							return {
								x: categoriesResult.indexOf(obj.supplierCategory),
								y: obj.weightedAverageMargin,
								tooltip: generateToolTip({
									[obj.supplierCategory]:
										obj.weightedAverageMargin.toFixed(2) + '%',
								}),
							};
						}),
					].sort((a, b) => a.x - b.x) || [];
				const grossAmount =
					[
						...response.data.map(obj => ({
							x: categoriesResult.indexOf(obj.supplierCategory),
							y: obj.totalGrossAmount,
							tooltip: generateToolTip({
								[obj.supplierCategory]: formatSpendWithDecimalAndSymbol(
									obj.totalGrossAmount,
									'$',
									false,
									1
								),
							}),
						})),
					].sort((a, b) => a.x - b.x) || [];

				dispatch({
					type: actionTypes.FETCH_CHART_DATA_SUCCESS,
					avgMarginBySupplierData: {
						columnChartData: grossAmount,
						lineChartData: weightedAvgMargin,
						xAxis: categoriesResult,
					},
				});
			})
			.then(() => {
				dispatch({
					type: actionTypes.SET_AVGMARGINBYSUPPLIER_LOADER,
					loading: false,
				});
			});
	}, []);

	useEffect(() => {
		dispatch({
			type: actionTypes.SET_AVGMARGINGROSSSPEND_LOADER,
			loading: true,
		});
		getAvgMarginByGrossSpendByGroupData()
			.then(response => {
				const sortedData = response.data.sort(
					(a, b) => b.totalGrossAmount - a.totalGrossAmount
				);
				const categoriesResult = [
					...sortedData
						.map(obj => obj.groupCategory)
						.filter(category => !category.includes('Other')),
					...sortedData
						.map(obj => obj.groupCategory)
						.filter(category => category.includes('Other')),
				];

				const weightedAvgMargin =
					sortedData
						.map(obj => {
							return {
								x: categoriesResult.indexOf(obj.groupCategory),
								y: obj.weightedAverageMargin,
								tooltip: generateToolTip({
									[obj.groupCategory]:
										obj.weightedAverageMargin.toFixed(2) + '%',
								}),
							};
						})
						.sort((a, b) => a.x - b.x) || [];
				const grossAmount =
					sortedData
						.map(obj => ({
							x: categoriesResult.indexOf(obj.groupCategory),
							y: obj.totalGrossAmount,
							tooltip: generateToolTip({
								[obj.groupCategory]: formatSpendWithDecimalAndSymbol(
									obj.totalGrossAmount,
									'$',
									false,
									1
								),
							}),
						}))
						.sort((a, b) => a.x - b.x) || [];

				dispatch({
					type: actionTypes.FETCH_CHART_DATA_SUCCESS,
					avgMarginGrossSpendData: {
						columnChartData: grossAmount,
						lineChartData: weightedAvgMargin,
						xAxis: categoriesResult,
					},
				});
			})
			.then(() => {
				dispatch({
					type: actionTypes.SET_AVGMARGINGROSSSPEND_LOADER,
					loading: false,
				});
			});
	}, []);

	useEffect(() => {
		dispatch({
			type: actionTypes.SET_AVGMARGINBYJOBTITLE_LOADER,
			loading: true,
		});
		getAvgMarginByJobTitleData()
			.then(response => {
				const categoriesResult = [
					...response.data
						.map(obj => obj.jobTitleCategory)
						.filter(category => !category.includes('Other')),
					...response.data
						.map(obj => obj.jobTitleCategory)
						.filter(category => category.includes('Other')),
				];

				const weightedAvgMargin =
					response.data
						.map(obj => {
							return {
								x: categoriesResult.indexOf(obj.jobTitleCategory),
								y: obj.weightedAverageMargin,
								tooltip: generateToolTip({
									[obj.jobTitleCategory]:
										obj.weightedAverageMargin.toFixed(2) + '%',
								}),
							};
						})
						.sort((a, b) => a.x - b.x) || [];
				const grossAmount =
					response.data
						.map(obj => ({
							x: categoriesResult.indexOf(obj.jobTitleCategory),
							y: obj.totalGrossAmount,
							tooltip: generateToolTip({
								[obj.jobTitleCategory]: formatSpendWithDecimalAndSymbol(
									obj.totalGrossAmount,
									'$',
									false,
									1
								),
							}),
						}))
						.sort((a, b) => a.x - b.x) || [];

				dispatch({
					type: actionTypes.FETCH_CHART_DATA_SUCCESS,
					avgMarginByJobTitleData: {
						columnChartData: grossAmount,
						lineChartData: weightedAvgMargin,
						xAxis: categoriesResult,
					},
				});
			})
			.then(() => {
				dispatch({
					type: actionTypes.SET_AVGMARGINBYJOBTITLE_LOADER,
					loading: false,
				});
			});
	}, []);
	// Provide the state and actions to the children
	const contextValue = {
		state,
		actions: {
			setChartData,
			setLoader,
			// fetchChartData,
			// Add more actions as needed
		},
	};

	return (
		<ChartContext.Provider value={contextValue}>
			{children}
		</ChartContext.Provider>
	);
};

// Create a custom hook for using the context
const useChartContext = () => {
	const context = useContext(ChartContext);
	if (!context) {
		throw new Error(
			'useChartContext must be used within a ChartContextProvider'
		);
	}
	return context;
};

export { ChartContextProvider, useChartContext };

// Usage
// Import statements for React and other dependencies

// import { ChartContextProvider, useChartContext } from './ChartContextProvider';

// const Chart1 = () => {
//   const { state, actions } = useChartContext();
//   const { chartData1 } = state;
//   // Access other chart data as needed
// actions.fetchChartData is used to call
//   // Render chart based on chart data

//   return (
//     <div>
//       <h2>Chart 1</h2>
//       {/* Render chart content */}
//       <p>Chart Data 1: {JSON.stringify(chartData1)}</p>
//       {/* Render other chart content */}
//     </div>
//   );
// };

// const Chart2 = () => {
//   const { state, actions } = useChartContext();
//   const { chartData2 } = state;
//   // Access other chart data as needed

//   // Render chart based on chart data

//   return (
//     <div>
//       <h2>Chart 2</h2>
//       {/* Render chart content */}
//       <p>Chart Data 2: {JSON.stringify(chartData2)}</p>
//       {/* Render other chart content */}
//     </div>
//   );
// };
